import { useTableColumns } from 'components/InstructorFormativeSubmissions/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import chatService from 'services/chat';
import feedbackService from 'services/feedback';
import submissionService from 'services/submission';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { FEEDBACK_LIMIT } from 'utils/constants';
import { downloadCSV, getUserPreferredFullName, stripHTML } from 'utils/helper';

const handleFilters = (data, filters, setData) => {
    let res = [...data];

    res = res.filter((item) => {
        const searchCond =
            getUserPreferredFullName(item).toLowerCase().includes(filters.search.toLowerCase()) ||
            item.email.toLowerCase().includes(filters.search.toLowerCase());
        const statusCond = !filters.status.length
            ? true
            : filters.status.some((status) => {
                  if (status === 'draft') {
                      return item.submissions.length > 0;
                  }
                  if (status === 'no-draft') {
                      return item.submissions.length === 0;
                  }
                  return false;
              });
        return searchCond && statusCond;
    });

    setData(res);
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { studentAccountUsers, currentAssignmentId, currentAssignment, studentSubmissions, setStudentSubmissions } =
        useApp();
    const { auth } = useAuth();
    const { closeDialog, setDialog } = useDialog();
    const history = useHistory();
    const { t } = useTranslation('assignment');

    const [data, setData] = useState([]);
    const [checked, setChecked] = useState([]);
    const [filters, setFilters] = useState({
        open: false,
        status: [],
        search: '',
    });

    const columns = useTableColumns();

    const {
        sortedFilteredData: studentSubmissionsGroupedByAccountUser,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(data, columns, filters, setData, handleFilters);

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            status: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.status.length) count++;
        return count;
    };

    const onClickBack = () => {
        history.push(`/assignments/${currentAssignmentId}`);
    };

    const onClickView = ({ owner_account_id }) => {
        const params = new URLSearchParams({ owner_account_id, type: 'formative' });
        history.push(`/assignments/${currentAssignmentId}/submissions?${params.toString()}`);
    };

    const resetChats = async (submissions = []) => {
        if (!currentAssignmentId || !auth?.course_id) return;

        const ownerAccountIds = [
            ...new Set(submissions.map(({ owner_account_id }) => owner_account_id).filter(Boolean)),
        ];

        await Promise.all(
            ownerAccountIds.map(async (ownerAccountId) => {
                const assignmentChat = await chatService.getAssignmentChatSummary({
                    assignment_id: currentAssignmentId,
                    course_id: auth.course_id,
                    owner_account_id: ownerAccountId,
                });

                if (!assignmentChat?.id) return;

                await chatService.deleteChat(assignmentChat.id);
            })
        );
    };

    const resetSubmissions = () => {
        setDialog({
            open: true,
            message: t('dialog.reset-drafts-warning.title', 'Reset All Drafts?'),
            description: t(
                'dialog.reset-drafts-warning.description',
                'All drafts and associated feedback will be permanently deleted.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setStudentSubmissions([]);
                closeDialog();
                await Promise.all(
                    studentSubmissions.map(async ({ id, is_legacy }) => {
                        return is_legacy
                            ? await feedbackService.deleteFeedback(id)
                            : await submissionService.deleteSubmission(id);
                    })
                );
                await resetChats(studentSubmissions);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const resetCheckedSubmissions = () => {
        setDialog({
            open: true,
            message: t('dialog.delete-drafts-warning.title', 'Delete All Selected Drafts?'),
            description: t(
                'dialog.delete-drafts-warning.description',
                'All selected drafts and associated feedback will be permanently deleted.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                const submissionsToDelete = studentSubmissions.filter(({ owner_account_id }) =>
                    checked.includes(owner_account_id)
                );
                setChecked([]);
                setStudentSubmissions((current) => {
                    return current.filter(({ id }) => !submissionsToDelete.map(({ id }) => id).includes(id));
                });
                closeDialog();
                await Promise.all(
                    submissionsToDelete.map(async ({ id, is_legacy }) => {
                        return is_legacy
                            ? await feedbackService.deleteFeedback(id)
                            : await submissionService.deleteSubmission(id);
                    })
                );
                await resetChats(submissionsToDelete);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const exportToCSV = () => {
        const settings = currentAssignment.ai_student_feedback_settings ?? currentAssignment.ai_feedback_settings;
        const structure = settings?.structure;
        const templatedMap = settings?.templated || {};

        const buildSystemFeedback = (submission) => {
            const commentary = submission.ai?.feedback?.commentary || '';
            const suggestionTexts = (submission.ai?.feedback?.improvement_suggestions || [])
                .map((s) => stripHTML(s.text))
                .filter(Boolean);
            return [commentary, ...suggestionTexts].filter(Boolean).join('\n\n');
        };

        const buildRubricFeedback = (submission, criterionId) => {
            const suggestions = submission.ai?.feedback?.improvement_suggestions || [];
            const suggestion = suggestions.find((item) => item.criterion_id === criterionId);
            if (!suggestion) return '';
            const positive = suggestion.positive?.text || '';
            const negative = suggestion.negative?.text || '';
            return [positive, negative].filter(Boolean).join('\n\n');
        };

        const buildTemplatedFeedback = (submission, criterionId) => {
            const optionId = submission.ai?.grading?.[criterionId]?.optionId;
            if (!optionId) return '';
            return templatedMap?.[criterionId]?.[optionId] || '';
        };

        const buildCustomFeedback = (submission) => {
            return submission.ai?.feedback?.commentary || '';
        };

        const isNonRubric = structure === 'system' || structure === 'custom';

        const csv = [
            [
                t('common:table.student-identifier', 'Student Identifier'),
                t('common:table.section-s', 'Section(s)'),
                t('common:table.criterion', 'Criterion'),
                ...Array.from({ length: FEEDBACK_LIMIT }).map((_, i) =>
                    t('draft-number-feedback', 'Draft {count} Feedback', { count: i + 1 })
                ),
            ],

            ...studentSubmissionsGroupedByAccountUser.flatMap((accountUser) => {
                const studentIdentifier = getUserPreferredFullName(accountUser);
                const sections = accountUser.sections.map((s) => s.name).join(', ');

                if (isNonRubric) {
                    const row = [studentIdentifier, sections, t('common:ui.na', 'N/A')];

                    for (let i = 0; i < FEEDBACK_LIMIT; i++) {
                        const submission = accountUser.submissions[i];

                        if (!submission) {
                            row.push('');
                            continue;
                        }

                        if (structure === 'system') {
                            row.push(buildSystemFeedback(submission));
                        } else {
                            row.push(buildCustomFeedback(submission));
                        }
                    }

                    return [row];
                }

                return currentAssignment.rubric.criteria.map((criterion, idx) => {
                    const row = [idx === 0 ? studentIdentifier : '', idx === 0 ? sections : '', criterion.title];

                    for (let i = 0; i < FEEDBACK_LIMIT; i++) {
                        const submission = accountUser.submissions[i];

                        if (!submission) {
                            row.push('');
                            continue;
                        }

                        if (structure === 'rubric') {
                            row.push(buildRubricFeedback(submission, criterion.id));
                        } else if (structure === 'templated') {
                            row.push(buildTemplatedFeedback(submission, criterion.id));
                        } else {
                            row.push('');
                        }
                    }

                    return row;
                });
            }),
        ];

        downloadCSV(`(FormativeFeedback) ${currentAssignment.title}_FormativeFeedbackSummaryExport.csv`, csv);
    };

    useEffect(() => {
        setData(
            studentAccountUsers?.map((accountUser) => {
                return {
                    ...accountUser,
                    submissions: (studentSubmissions ?? []).filter(
                        ({ owner_account_id }) => owner_account_id === accountUser.owner_account_id
                    ),
                };
            })
        );
    }, [studentAccountUsers, studentSubmissions]);

    return (
        <PageContext.Provider
            value={{
                currentAssignment,
                studentSubmissionsGroupedByAccountUser,
                checked,
                setChecked,
                filters,
                setFilters,
                columns,
                sortState,
                handleSetSortState,
                resetFilters,
                getFilterCount,
                onClickBack,
                onClickView,
                resetSubmissions,
                resetCheckedSubmissions,
                exportToCSV,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
